import React, { PropsWithChildren, useContext, useState } from "react";
import { View, ScrollView, ViewStyle, StyleProp } from "react-native";
import {
  Box,
  Space,
  TouchableBox,
  useLayoutContext,
  Plus,
  BoxProps,
  Image,
  ImageProps,
} from "arkh";
import { BottomSheetScrollView } from "@gorhom/bottom-sheet";

const AHImageCollectionListItemWrapper_ = (props: {
  children: React.ReactNode;
  shadow?: BoxProps["shadow"] | "none";
  onPress?(): void;
}) => {
  const { size, space, spaceBetween } = useContext(
    AHImageHorizontalCollectionContext
  );
  return (
    <Box space={space} spaceRight={spaceBetween}>
      <Box
        color="surface-primary"
        shadow={props.shadow === "none" ? undefined : (props.shadow ?? "weak")}
        cornerRadius="medium"
      >
        <Box
          cornerRadius="medium"
          style={[
            {
              overflow: "hidden",
              width: size,
              height: size,
            },
          ]}
        >
          <TouchableBox
            style={{
              flex: 1,
            }}
            onPress={props.onPress}
          >
            {props.children}
          </TouchableBox>
        </Box>
      </Box>
    </Box>
  );
};

export const AHImageCollectionListItemWrapper = React.memo(
  AHImageCollectionListItemWrapper_
);

export const AHImageHorizontalCollectionItem = ({
  onPress,
  shadow,
  onRemove,
  ...props
}: ImageProps & {
  onRemove?(): void;
  onPress?(): void;
  shadow?: BoxProps["shadow"] | "none";
}) => {
  return (
    <AHImageCollectionListItemWrapper onPress={onPress} shadow={shadow}>
      <Image style={{ flex: 1 }} {...props} />
    </AHImageCollectionListItemWrapper>
  );
};

export const AHImageHorizontalCollectionAddItem = ({
  onPress,
  shadow,
  ref,
}: {
  onPress: () => void;
  ref?: React.RefObject<View | null>;
  shadow?: BoxProps["shadow"] | "none";
}) => {
  const { size, space, spaceBetween } = useContext(
    AHImageHorizontalCollectionContext
  );
  return (
    <TouchableBox
      onPress={onPress}
      ref={ref}
      space={space}
      spaceRight={spaceBetween}
    >
      <Box
        cornerRadius="medium"
        color="background"
        style={{
          height: size,
          width: size,
          overflow: "hidden",
          alignContent: "center",
          justifyContent: "center",
        }}
      >
        <Plus
          style={{
            alignSelf: "center",
          }}
        />
      </Box>
    </TouchableBox>
  );
};

export const AHHorizontalCollection: React.FC<
  PropsWithChildren<{
    snapToInterval?: number;
    useBottomSheetScrollView?: boolean;
    contentContainerStyle?: StyleProp<ViewStyle>;
  }>
> = ({
  children,
  contentContainerStyle,
  snapToInterval = 100,
  useBottomSheetScrollView,
}) => {
  const SV = useBottomSheetScrollView ? BottomSheetScrollView : ScrollView;
  return (
    <SV
      style={{ alignSelf: "stretch", flex: 1 }}
      snapToInterval={snapToInterval}
      snapToAlignment="start"
      decelerationRate="fast"
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={[
        {
          justifyContent: "flex-start",
          flexDirection: "row",
        },
        contentContainerStyle,
      ]}
      horizontal
      showsVerticalScrollIndicator={false}
    >
      {children}
    </SV>
  );
};

const AHImageHorizontalCollectionContext = React.createContext<{
  size: number;
  space: Space;
  spaceBetween: Space;
}>({ size: 0, space: "small", spaceBetween: "small" });

type AHImageHorizontalCollectionProps = {
  imagesToShow?: number;
  verticalSpace?: Space;
  contentContainerStyle?: StyleProp<ViewStyle>;
  useBottomSheetScrollView?: boolean;
};

export function AHImageHorizontalCollectionStaticSize({
  size,
  space,
  children,
  useBottomSheetScrollView,
}: React.PropsWithChildren<{
  size: number;
  space: Space;
  useBottomSheetScrollView?: boolean;
}>) {
  const layout = useLayoutContext();
  const padding = layout.space[space];
  return (
    <View
      style={{
        height: size + padding * 2,
      }}
    >
      <AHHorizontalCollection
        snapToInterval={size + padding}
        contentContainerStyle={{ paddingRight: layout.space.medium }}
        useBottomSheetScrollView={useBottomSheetScrollView}
      >
        <AHImageHorizontalCollectionContext.Provider
          value={{ size, space, spaceBetween: "none" }}
        >
          {children}
        </AHImageHorizontalCollectionContext.Provider>
      </AHHorizontalCollection>
    </View>
  );
}

export function AHImageHorizontalCollection({
  imagesToShow = 3,
  contentContainerStyle,
  children,
  verticalSpace = "small",
  useBottomSheetScrollView,
}: React.PropsWithChildren<AHImageHorizontalCollectionProps>) {
  const layout = useLayoutContext();
  const [collectionWidth, setCollectionWidth] = useState<number | null>(null);
  const { space } = useLayoutContext();

  const imageSize = collectionWidth
    ? collectionWidth / imagesToShow - space.small * 2
    : 0;

  const size = collectionWidth ? imageSize - imageSize / (6 * imagesToShow) : 0;

  return (
    <View
      style={{
        width: "100%",
        height: size + layout.space[verticalSpace] * 2,
        paddingVertical: layout.space[verticalSpace],
      }}
      onLayout={({ nativeEvent: { layout } }) => {
        setCollectionWidth(layout.width);
      }}
    >
      <AHHorizontalCollection
        snapToInterval={size}
        contentContainerStyle={contentContainerStyle}
        useBottomSheetScrollView={useBottomSheetScrollView}
      >
        <AHImageHorizontalCollectionContext.Provider
          value={{ size, space: "none", spaceBetween: "medium" }}
        >
          {children}
        </AHImageHorizontalCollectionContext.Provider>
      </AHHorizontalCollection>
    </View>
  );
}
